/**
 * Modal to edit the signed-in user's profile.
 */
import * as React from 'react';
import { Mail, NotepadText } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { getUser, updateUserProfile } from '@/services/users.service';
import type { UpdateUserProfileDto } from '@/types/user.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { profileEditValidationSchema } from '@/utils/validation-schemas-utils';
import { useAuthStore } from '@/stores/authStore';
import {
  commitSplitContactSwap,
  canSwapPrimaryContact,
  areContactsIdentical,
  isPrimaryContactFormValid,
  isPrimaryContactSubmitBlocked,
  primaryContactFieldError,
  isPrimaryContactDirty,
  phoneToE164,
  readPhoneSetupLink,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { ContactChangeModal } from '@/components/common/modals/ContactChangeModal';
import { useContactChangeOnSubmit } from '@/hooks/useContactChangeOnSubmit';
import {
  applyProfilePrimaryContactChange,
  buildUpdateProfileDto,
  hydrateEditFormFromUser,
} from '@/components/profile/profile-form-utils';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';

/** Props for {@link EditProfileModal}. */
interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
}

/** Modal to edit the signed-in user's profile. */
export const EditProfileModal: React.FC<EditProfileModalProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { user, setUser } = useAuthStore();
  const defaultCountry = useDefaultPhoneCountry();
  const [formData, setFormData] = React.useState<UpdateUserProfileDto>({});
  const [phoneCountryCode, setPhoneCountryCode] = React.useState<string>(defaultCountry);
  const [primaryContact, setPrimaryContact] = React.useState('');
  const [primaryContactType, setPrimaryContactType] = React.useState<'email' | 'phone'>('email');
  const [primaryContactCountryCode, setPrimaryContactCountryCode] = React.useState(defaultCountry);

  const validation = useFormValidation(profileEditValidationSchema);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: () => getUser(user.id),
    enabled: open && !!user?.id,
  });

  React.useEffect(() => {
    if (profile) {
      const hydrated = hydrateEditFormFromUser(profile, defaultCountry);
      setFormData(hydrated.initialData);
      setPhoneCountryCode(hydrated.phoneCountryCode);
      setPrimaryContact(hydrated.primaryContact);
      setPrimaryContactType(hydrated.primaryContactType);
      setPrimaryContactCountryCode(hydrated.primaryContactCountryCode);
      validation.validateInitialFormData(hydrated.initialData);
    }
  }, [profile, defaultCountry]);

  const secondaryContactValue =
    primaryContactType === 'email' ? formData.phone || '' : formData.email || '';

  const { isChecking: isPrimaryChecking, exists: primaryContactExists } = useContactExistsCheck({
    value: primaryContact,
    type: resolveContactCheckType(primaryContactType),
    excludeUserId: user?.id,
    skipHydratedValue: true,
  });

  const contactsAreIdentical = areContactsIdentical(primaryContact, secondaryContactValue);
  const primaryContactExistsError = primaryContactFieldError(
    primaryContactExists,
    primaryContactType,
    contactsAreIdentical,
  );

  const isPrimaryDirty = isPrimaryContactDirty({
    mode: primaryContactType,
    currentValue: primaryContact,
    storedEmail: profile?.email,
    storedPhone: profile?.phone,
    serverKind: profile?.primaryContactKind,
    defaultCountry,
  });

  const contactChange = useContactChangeOnSubmit({
    isDirty: isPrimaryDirty,
    kind: primaryContactType,
    ownProfile: true,
    closeForm: onClose,
    fallbackPhone:
      primaryContactType === 'phone'
        ? phoneToE164(primaryContact, primaryContactCountryCode || defaultCountry)
        : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateUserProfileDto) => updateUserProfile(user.id, dto),
    onSuccess: (updatedUser) => {
      if (contactChange.isIntercepting) {
        contactChange.onMutationSettled({ ok: true, link: readPhoneSetupLink(updatedUser) });
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
      void queryClient.invalidateQueries({ queryKey: ['admins'] });
      void queryClient.invalidateQueries({ queryKey: ['volunteers'] });

      if (user) {
        setUser({
          ...user,
          firstName: updatedUser.firstName || user.firstName,
          lastName: updatedUser.lastName || user.lastName,
          displayName: updatedUser.displayName || user.displayName,
          email: updatedUser.email || user.email,
          phone: updatedUser.phone || user.phone,
          status: updatedUser.status || user.status,
        });
      }

      toast.success('Profil modifié avec succès !');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
      contactChange.onMutationSettled({ ok: false });
    },
  });

  /** Set one field and re-validate (phone uses `_phoneCountryCode` context). */
  const handleFieldChange = (field: keyof UpdateUserProfileDto, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    const updatedData = { ...formData, [field]: value };

    if (field === 'phone') {
      validation.validateField(field, value, {
        ...updatedData,
        _phoneCountryCode: phoneCountryCode,
      });
    } else {
      validation.validateField(field, value, updatedData);
    }

    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Swap primary and secondary, then re-validate the contact field. */
  function handleSwapContacts(): void {
    setFormData((prev) =>
      commitSplitContactSwap({
        primaryContact,
        primaryContactType,
        primaryCountryCode: primaryContactCountryCode,
        formData: prev,
        secondaryCountryCode: phoneCountryCode,
        setPrimaryContact,
        setPrimaryContactType,
        setPrimaryContactCountryCode,
        setSecondaryCountryCode: setPhoneCountryCode,
        validation,
      }),
    );
  }

  /** Patch primary contact, keep form email/phone in sync, re-validate. */
  function handlePrimaryContactChange(
    value: string,
    type: 'email' | 'phone',
    countryCode?: string,
  ): void {
    setPrimaryContact(value);
    setPrimaryContactType(type);
    if (countryCode) setPrimaryContactCountryCode(countryCode);
    const updated = applyProfilePrimaryContactChange(formData, value, type);
    setFormData(updated);
    validation.validateField('contact', value, {
      ...updated,
      _contactType: type,
      contactCountryCode: countryCode ?? primaryContactCountryCode,
      _phoneCountryCode:
        type === 'email' ? phoneCountryCode : (countryCode ?? primaryContactCountryCode),
      _primaryContactExists: primaryContactExists,
    });
    validation.markAsTouched('contact');
    validation.markAsDirty('contact');
  }

  /** Validate then PATCH the signed-in user's profile. */
  function handleSubmit(): void {
    if (
      isPrimaryContactSubmitBlocked(primaryContactExists, isPrimaryChecking, contactsAreIdentical)
    )
      return;
    const isValid = validation.validateForm({
      ...formData,
      contact: primaryContact,
      _contactType: primaryContactType,
      contactCountryCode: primaryContactCountryCode,
      _phoneCountryCode:
        primaryContactType === 'email' ? phoneCountryCode : primaryContactCountryCode,
      _primaryContactExists: primaryContactExists,
    });

    if (!isValid) return;

    contactChange.requestSubmit(() => {
      updateMutation.mutate(
        buildUpdateProfileDto({
          firstName: formData.firstName,
          lastName: formData.lastName,
          primaryContact,
          primaryContactType,
          primaryContactCountryCode,
          secondaryEmail: formData.email,
          secondaryPhone: formData.phone,
          secondaryPhoneCountryCode: phoneCountryCode,
        }),
      );
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier mon profil</DialogTitle>
            <DialogDescription>Modifiez vos informations personnelles</DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <div className="space-y-6 py-4">
                <div className="form-section">
                  <h4 className="form-section-title">
                    <NotepadText className="w-4 h-4" />
                    Informations personnelles
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Prénom"
                      value={formData.firstName || ''}
                      onChange={(e) => handleFieldChange('firstName', e.target.value)}
                      onBlur={() => handleBlur('firstName')}
                      placeholder="Ex: Marie"
                      maxLength={100}
                      state={validation.getFieldState('firstName')}
                      error={validation.getFieldError('firstName')}
                    />
                    <Input
                      label="Nom"
                      value={formData.lastName || ''}
                      onChange={(e) => handleFieldChange('lastName', e.target.value)}
                      onBlur={() => handleBlur('lastName')}
                      placeholder="Ex: Martin"
                      maxLength={100}
                      state={validation.getFieldState('lastName')}
                      error={validation.getFieldError('lastName')}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 mt-4">
                    <SmartContactInput
                      label="Contact principal (identifiant)"
                      value={primaryContact}
                      onContactChange={handlePrimaryContactChange}
                      storedContact={{
                        primaryKind: primaryContactType,
                        serverPrimaryKind: profile?.primaryContactKind,
                        email: profile?.email,
                        phone: profile?.phone,
                      }}
                      error={primaryContactExistsError ?? validation.getFieldError('contact')}
                      state={
                        primaryContactExistsError ? 'error' : validation.getFieldState('contact')
                      }
                      showContactActions
                      emailVerified={profile?.emailVerified}
                      userId={user?.id}
                      ownProfile
                      swapButton={
                        canSwapPrimaryContact(primaryContactType) ? (
                          <Button
                            type="button"
                            preset="contact-swap"
                            size="md"
                            onClick={handleSwapContacts}
                          />
                        ) : undefined
                      }
                    />
                    {primaryContactType === 'email' ? (
                      <PhoneInput
                        label="Numéro de téléphone (contact secondaire, optionnel)"
                        value={formData.phone || ''}
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                        onBlur={() => handleBlur('phone')}
                        onCountryChange={(country) => setPhoneCountryCode(country.code)}
                        countryCode={phoneCountryCode}
                        state={validation.getFieldState('phone')}
                        error={validation.getFieldError('phone')}
                        helperText="Numéro secondaire pour vous joindre"
                      />
                    ) : (
                      <Input
                        type="text"
                        label="Email (contact secondaire, optionnel)"
                        value={formData.email || ''}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        onBlur={() => handleBlur('email')}
                        icon={Mail}
                        placeholder="Ex: contact@example.com"
                        state={validation.getFieldState('email')}
                        error={validation.getFieldError('email')}
                        helperText="Email secondaire (le téléphone reste l'identifiant de connexion)"
                      />
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  preset="form-cancel"
                  onClick={onClose}
                  disabled={updateMutation.isPending || contactChange.modal.open}
                >
                  Annuler
                </Button>
                <Button
                  preset="form-save"
                  onClick={handleSubmit}
                  loading={updateMutation.isPending}
                  disabled={
                    !isPrimaryContactFormValid({
                      primaryExists: primaryContactExists,
                      isPrimaryChecking,
                      identical: contactsAreIdentical,
                      formValid: validation.isFormValid(),
                    }) ||
                    updateMutation.isPending ||
                    contactChange.modal.open
                  }
                >
                  Enregistrer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ContactChangeModal
        open={contactChange.modal.open}
        kind={contactChange.modal.kind}
        ownProfile={contactChange.modal.ownProfile}
        phase={contactChange.modal.phase}
        phoneSetupLink={contactChange.modal.phoneSetupLink}
        onClose={contactChange.cancel}
        onConfirm={contactChange.confirm}
        onCopyLink={contactChange.onCopyLink}
      />
    </>
  );
};
